/**
 * src/ui/player.ts — a SUPERFÍCIE de mídia: dois elementos, reutilizados para sempre.
 *
 * A regra deste arquivo inteiro cabe em uma frase: **`<img>` e `<video>` são
 * criados UMA vez e só mudam de `src`.** Recriar o elemento a cada item é o que
 * produz o flash preto entre anúncios (PLAYER §4.2, e o comentário em `index.html`).
 * Em TV Box isso também vaza memória — cada nó novo é lixo para o coletor.
 *
 * O que este arquivo faz com o motor (`src/player/engine.ts`):
 *  - Implementa `EngineHost.show`: baixa a mídia, troca a fonte, e só RESOLVE
 *    quando a mídia está no ar (`img.decode()` / `video.canplay`). O motor espera
 *    essa promise para marcar o item como exibido — sem isso, o "tempo do item"
 *    começaria a contar antes de o cliente ver qualquer coisa.
 *  - Agenda o avanço: imagem por `setTimeout(holdSeconds)`, vídeo pelo evento
 *    `ended` (mais um watchdog de segurança). Nenhum dos dois é do motor, porque só
 *    aqui existe DOM.
 *
 * MUDO E AUTOPLAY (W2/D7): `<video muted playsinline>` e `video.muted = true` em
 * TODOS os caminhos. Sem isso o Chrome bloqueia o autoplay com áudio e a TV fica
 * numa tela de espera de clique — o pior modo de falha para um receptor.
 */
import type { DisplayItem } from '../player/queue';
import type { DisplayFit, EngineHost } from '../player/engine';
import type { ReceptorGateway } from '../api/types';

export interface PlayerSurfaceOptions {
  root: HTMLElement;
  gateway: ReceptorGateway;
  /** Chamado quando o item termina (imagem por tempo, vídeo por `ended`). */
  onEnded: (reason: 'timer' | 'media-end') => void;
  /** Diagnóstico; nunca exibido na TV. */
  onLog?: (event: string, detail?: Record<string, unknown>) => void;
}

/** Motivo de falha ao exibir — o motor pula o item em qualquer um deles. */
export class MediaUnavailableError extends Error {
  readonly mediaId: string;
  /** Motivo original — vai para o log, nunca para a tela (§5.2). */
  readonly reason: unknown;

  constructor(mediaId: string, reason: unknown) {
    super(`Mídia indisponível: ${mediaId}`);
    this.name = 'MediaUnavailableError';
    this.mediaId = mediaId;
    this.reason = reason;
  }
}

/**
 * A superfície. Um `EngineHost` pronto — o motor e esta classe não se conhecem
 * diretamente; o `main.ts` liga os dois.
 */
export class PlayerSurface implements EngineHost {
  private readonly root: HTMLElement;
  private readonly gateway: ReceptorGateway;
  private readonly onEnded: (reason: 'timer' | 'media-end') => void;
  private readonly emit: (event: string, detail?: Record<string, unknown>) => void;

  private readonly img: HTMLImageElement;
  private readonly video: HTMLVideoElement;

  /** `objectURL` do blob atual — revogado ANTES de trocar (vazamento em TV Box). */
  private currentUrl: string | null = null;
  private timer: ReturnType<typeof setTimeout> | null = null;
  private watchdog: ReturnType<typeof setInterval> | null = null;
  /** `true` enquanto um `show` está em voo: um `clear` pode invalidá-lo. */
  private generation = 0;
  private videoOnAir = false;
  /** `true` enquanto um `show` está em voo: um `clear` pode invalidá-lo. */
  private watchdogExpired: () => boolean = () => false;

  constructor(options: PlayerSurfaceOptions) {
    this.root = options.root;
    this.gateway = options.gateway;
    this.onEnded = options.onEnded;
    this.emit = options.onLog ?? (() => {});

    const img = this.root.querySelector('#surface-image');
    const video = this.root.querySelector('#surface-video');
    if (!(img instanceof HTMLImageElement) || !(video instanceof HTMLVideoElement)) {
      throw new Error('player: #surface-image e #surface-video precisam existir no index.html');
    }
    this.img = img;
    this.video = video;

    // Mudo em todas as trilhas (W2/D7). O atributo `muted` no HTML sozinho não basta
    // em alguns navegadores quando o src muda por JS.
    this.video.muted = true;
    this.video.defaultMuted = true;
    this.video.playsInline = true;
    this.video.setAttribute('playsinline', '');
    this.video.setAttribute('muted', '');

    this.video.addEventListener('ended', this.handleVideoEnded);
    this.video.addEventListener('error', this.handleVideoError);
  }

  /** Liga o watchdog de vídeo (o motor é quem sabe se o orçamento estourou). */
  setVideoWatchdog(expired: () => boolean): void {
    this.watchdogExpired = expired;
  }

  /** `EngineHost.onLog` — diagnóstico. Vai para o console/log, NUNCA para a TV. */
  onLog(event: string, detail?: Record<string, unknown>): void {
    this.emitLog(event, detail);
  }

  /** `EngineHost.onItemChanged` — a casca usa para o `currentAdId` do heartbeat. */
  onItemChanged(_item: DisplayItem | null): void {
    // A superfície não precisa saber; quem gerencia o heartbeat é a casca (main.ts),
    // que implementa o host composto. Deixado como no-op explícito para o tipo.
  }

  /** `EngineHost.show` — coloca o item no ar e só resolve quando está EXIBINDO. */
  async show(command: { item: DisplayItem; fit: DisplayFit }): Promise<void> {
    const { item, fit } = command;
    const generation = ++this.generation;
    this.stopTimers();
    this.videoOnAir = false;

    // Aplica `object-fit` e rotação ANTES de trocar a fonte: trocar a fonte
    // re-renderiza, e aplicar estilo depois causaria um flash do estado
    // antigo. `contain`/`cover`/`stretch` mapeiam direto para `object-fit`
    // (PLAYER §4.5). A rotação vai num `transform: rotate` em vez de mexer
    // em `width/height` — assim o player não precisa conhecer a geometria
    // da tela e o container permanece o mesmo.
    const objectFit = fit;
    this.img.style.objectFit = objectFit;
    this.video.style.objectFit = objectFit;
    const rotation = item.rotation ?? 0;
    const rotationTransform = rotation === 0 ? '' : `rotate(${rotation}deg)`;
    this.img.style.transform = rotationTransform;
    this.video.style.transform = rotationTransform;

    let blob: Blob;
    try {
      blob = await this.withLoadTimeout(this.gateway.fetchMediaBytes(item.media));
    } catch (error) {
      // Regra 4 do §4.2: item sem mídia é PULADO, não trava o loop.
      throw new MediaUnavailableError(item.media?.id ?? item.adId, error);
    }
    if (generation !== this.generation) {
      return; // um `clear`/outro `show` aconteceu: este resultado já é obsoleto.
    }

    // Blob vazio = mídia que baixou mas não tem conteúdo (asset ausente na demo).
    if (!blob || blob.size === 0) {
      throw new MediaUnavailableError(item.media?.id ?? item.adId, 'blob vazio');
    }

    const url = URL.createObjectURL(blob);
    this.releaseCurrentUrl();
    this.currentUrl = url;

    if (item.kind === 'video') {
      await this.showVideo(item, url, generation);
    } else {
      await this.showImage(item, url, generation);
    }
  }

  private async showImage(item: DisplayItem, url: string, generation: number): Promise<void> {
    this.video.pause();
    this.video.removeAttribute('src');
    this.video.hidden = true;

    this.img.hidden = false;
    this.img.src = url;
    // `decode()` é o que garante "está no ar" antes de contarmos o tempo. Sem ele,
    // o item entraria no cronômetro enquanto a tela ainda está preta.
    if (typeof this.img.decode === 'function') {
      await this.withLoadTimeout(this.img.decode());
    }
    if (generation !== this.generation) {
      return;
    }
    this.emitLog('imagem no ar', { adId: item.adId, holdSeconds: item.holdSeconds });

    const seconds = Math.max(1, item.holdSeconds);
    this.timer = setTimeout(() => this.onEnded('timer'), seconds * 1000);
  }

  private async showVideo(item: DisplayItem, url: string, generation: number): Promise<void> {
    this.img.hidden = true;
    this.img.removeAttribute('src');

    this.video.hidden = false;
    this.video.muted = true;
    this.video.src = url;

    // A rejected play promise is a failed item, not successful playback.
    await this.withLoadTimeout(this.video.play());
    if (generation !== this.generation) {
      return;
    }
    this.videoOnAir = true;
    this.emitLog('vídeo no ar', { adId: item.adId, mediaDuration: item.media?.durationSeconds });

    // Watchdog: vídeo que nunca emite `ended` (codec esquisito, arquivo truncado)
    // TRAVARIA a TV num único anúncio. O orçamento é decidido pelo motor.
    let lastTime = this.video.currentTime;
    let stalledTicks = 0;
    this.watchdog = setInterval(() => {
      if (generation !== this.generation) return;
      const currentTime = this.video.currentTime;
      stalledTicks = currentTime > lastTime ? 0 : stalledTicks + 1;
      lastTime = currentTime;
      // Metadata is not a playback deadline (§4.2). Only stalled playback expires.
      if (stalledTicks >= 20 || (stalledTicks > 0 && this.watchdogExpired())) {
        this.emitLog('watchdog de vídeo estourou — avançando', { adId: item.adId });
        this.endVideo();
      }
    }, 1000);
  }

  /** `EngineHost.clear` — tira a mídia sem mostrar nada. Nunca deixa a tela preta por acidente. */
  async clear(_reason: 'new-queue-empty'): Promise<void> {
    this.generation += 1;
    this.videoOnAir = false;
    this.stopTimers();
    this.video.pause();
    this.video.removeAttribute('src');
    this.video.load();
    this.img.removeAttribute('src');
    this.releaseCurrentUrl();
    this.emitLog('superfície limpa');
  }

  private readonly handleVideoEnded = (): void => {
    if (this.videoOnAir && this.video.ended) this.endVideo();
  };
  private readonly handleVideoError = (): void => {
    if (this.videoOnAir) this.endVideo();
  };

  async destroy(): Promise<void> {
    this.video.removeEventListener('ended', this.handleVideoEnded);
    this.video.removeEventListener('error', this.handleVideoError);
    await this.clear('new-queue-empty');
  }

  private endVideo(): void {
    if (!this.videoOnAir) return;
    this.videoOnAir = false;
    this.stopTimers();
    this.onEnded('media-end');
  }

  /** Bound fetch/decode/play even when the browser never settles its promise. */
  private async withLoadTimeout<T>(operation: Promise<T>): Promise<T> {
    let timer: ReturnType<typeof setTimeout> | undefined;
    try {
      return await Promise.race([
        operation,
        new Promise<never>((_, reject) => {
          timer = setTimeout(() => reject(new Error('media load timed out')), 20_000);
        }),
      ]);
    } finally {
      clearTimeout(timer);
    }
  }

  private stopTimers(): void {
    if (this.timer !== null) {
      clearTimeout(this.timer);
      this.timer = null;
    }
    if (this.watchdog !== null) {
      clearInterval(this.watchdog);
      this.watchdog = null;
    }
  }

  private emitLog(event: string, detail?: Record<string, unknown>): void {
    this.emit(event, detail);
  }

  private releaseCurrentUrl(): void {
    if (this.currentUrl !== null) {
      URL.revokeObjectURL(this.currentUrl);
      this.currentUrl = null;
    }
  }

  /** Identifica qual elemento está no ar — usado pela verificação de demo. */
  activeElement(): 'image' | 'video' | 'none' {
    if (!this.video.hidden && this.video.getAttribute('src')) {
      return 'video';
    }
    if (!this.img.hidden && this.img.getAttribute('src')) {
      return 'image';
    }
    return 'none';
  }
}
